/* RESPONSIBLE TEAM: team-help-desk-experience */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { type TaskGenerator } from 'ember-concurrency';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type Store from '@ember-data/store';

interface Translation {
  [key: string]: string;
}

interface GlossaryItem {
  id: number;
  translations: Translation;
  non_translatable: boolean;
  created_at: string;
  updated_at: string;
}

let PAGE_SIZE = 100;

export default class GlossaryService extends Service {
  @service declare store: Store;
  @service declare appService: $TSFixMe;

  @tracked glossary: Array<GlossaryItem> = [];
  @tracked displayGlossary: Array<GlossaryItem> = [];
  @tracked availableLanguages: Set<string> = new Set();
  @tracked currentPage = 1;

  get hasLoadedGlossary() {
    return taskFor(this.loadGlossaryItems).performCount > 0;
  }

  async loadGlossary() {
    return taskFor(this.loadGlossaryItems).perform();
  }

  @task({ restartable: true }) *loadGlossaryItems(): TaskGenerator<void> {
    let response = yield fetch(`/ember/multilingual_glossary?app_id=${this.appService.app.id}`);
    let data = yield response.json();
    this.glossary = data;
    this.loadInitialPage();

    // Extract all unique language codes
    this.availableLanguages = new Set();
    for (let item of this.glossary) {
      if (item.translations) {
        Object.keys(item.translations).forEach((langCode) => {
          this.availableLanguages.add(langCode);
        });
      }
    }
  }

  loadInitialPage() {
    this.currentPage = 1;
    this.displayGlossary = this.glossary.slice(0, PAGE_SIZE);
  }

  loadMoreItems() {
    let nextPage = this.currentPage + 1;
    let startIndex = this.currentPage * PAGE_SIZE;
    let endIndex = nextPage * PAGE_SIZE;

    let moreItems = this.glossary.slice(startIndex, endIndex);
    this.displayGlossary = [...this.displayGlossary, ...moreItems];
    this.currentPage = nextPage;
  }

  get canLoadMore() {
    return this.displayGlossary.length < this.glossary.length;
  }

  get isLoading() {
    return taskFor(this.loadGlossaryItems).isRunning;
  }

  get totalItems() {
    return this.glossary.length;
  }
}

declare module '@ember/service' {
  interface Registry {
    glossaryService: GlossaryService;
  }
}
